/**
 * ScenarioExamplesModal — "what does this AI command do and what will I get
 * back?"
 *
 * Opened from any palette row's "Learn more". Built for at-a-glance reading
 * under incident stress: a one-line subtitle (what it is), the data sources the
 * capability uses as pills, the exact prompt with a one-click "Use prompt"
 * button (prefills the chat input — nothing is sent automatically), and — for
 * registry-scenario-backed commands — the when-to-use guidance plus expected
 * responses as collapsed accordions (one line per situation, tap to expand) so
 * users and QA can compare a real response against the documented expectation.
 */
import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewChip } from 'components/DesignSystems/New/StylishNewChip';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import ChatMarkdown from './ChatMarkdown';
import type { AICommand } from './hooks/useAICommands';

type ScenarioExamplesModalProps = {
  command: AICommand;
  onClose: () => void;
  /** Prefill "<command> <prompt>" into the chat input (not sent). */
  onUsePrompt: (command: AICommand) => void;
};

const SectionHeading: React.FC<{
  icon: React.ComponentProps<typeof SharedIcon>['iconName'];
  children: React.ReactNode;
}> = ({ icon, children }) => (
  <div className="ScenarioExamples-heading">
    <SharedIcon iconName={icon} size="sm" />
    {children}
  </div>
);

const ScenarioExamplesModal: React.FC<ScenarioExamplesModalProps> = ({
  command,
  onClose,
  onUsePrompt,
}) => {
  const [openExample, setOpenExample] = useState<string | null>(null);
  const scenario = command.scenario;

  return (
    <Modal
      show
      onHide={onClose}
      centered
      backdrop
      keyboard
      size="lg"
      scrollable
    >
      {/* align-items-start keeps the close button level with the title row
          instead of vertically centered against the stacked subtitle/chips. */}
      <Modal.Header
        closeButton
        closeVariant="white"
        className="align-items-start"
      >
        <div>
          <Modal.Title className="d-flex align-items-center gap-2">
            <SharedIcon iconName="auto_awesome" size="sm" />
            {command.label}
          </Modal.Title>
          <div className="ScenarioExamples-subtitle">{command.description}</div>
          {(command.dataSources?.length ?? 0) > 0 && (
            <div className="ScenarioExamples-sources">
              <span className="ScenarioExamples-sources-label">
                Data sources
              </span>
              {command.dataSources!.map((source) => (
                <StylishNewChip key={source} label={source} />
              ))}
            </div>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        {scenario && (
          <>
            <SectionHeading icon="lightbulb">When to use</SectionHeading>
            <p className="ScenarioExamples-guidance">{scenario.guidance}</p>
          </>
        )}

        <SectionHeading icon="prompt_suggestion">Prompt</SectionHeading>
        <div className="ScenarioExamples-prompt">
          <span className="ScenarioExamples-prompt-text">
            {command.command} {command.promptTemplate}
          </span>
          <StylishNewButton
            className="button--ai flex-shrink-0 gap-1"
            onClick={() => onUsePrompt(command)}
          >
            <SharedIcon iconName="auto_awesome" size="sm" /> Use prompt
          </StylishNewButton>
        </div>

        {scenario && (
          <>
            <SectionHeading icon="menu_book">What to expect</SectionHeading>
            <p className="ScenarioExamples-note">
              Illustrative examples — compare against your own incident.
            </p>
            {scenario.examples.map((example) => {
              const open = openExample === example.situation;
              return (
                <div
                  key={example.situation}
                  className="ScenarioExamples-example"
                >
                  <button
                    type="button"
                    className="ScenarioExamples-situation"
                    aria-expanded={open}
                    onClick={() =>
                      setOpenExample(open ? null : example.situation)
                    }
                  >
                    <SharedIcon
                      iconName={open ? 'expand_more' : 'chevron_right'}
                      size="sm"
                    />
                    {example.situation}
                  </button>
                  {open && (
                    <div className="ScenarioExamples-response">
                      <ChatMarkdown>{example.response}</ChatMarkdown>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <StylishNewButton className="button--secondary" onClick={onClose}>
          Close
        </StylishNewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ScenarioExamplesModal;
